import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, Eye, EyeOff, ShieldCheck, UserRound } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { authApi } from '@/api/auth';
import { extractError } from '@/api/client';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';

/** Longueur exigée côté serveur. Répétée ici pour le prévenir, pas pour le remplacer. */
const LONGUEUR_MINIMALE = 12;

/**
 * Mon compte — 25/09/2026.
 *
 * Jusqu'ici, personne ne pouvait changer son propre mot de passe : seul un
 * `admin_holding` déclenchait un lien de réinitialisation, transmis ensuite à
 * la main. Un mot de passe que son titulaire ne peut pas changer reste celui
 * que quelqu'un d'autre lui a communiqué.
 *
 * Route : /mon-compte — ouverte à tous les rôles, chacun n'agissant que sur
 * son propre compte.
 */
export const MonComptePage = () => {
  const user = useAuthStore((s) => s.user);

  const [actuel, setActuel]         = useState('');
  const [nouveau, setNouveau]       = useState('');
  const [confirmation, setConfirm]  = useState('');
  const [afficher, setAfficher]     = useState(false);

  const tropCourt      = nouveau.length > 0 && nouveau.length < LONGUEUR_MINIMALE;
  const identique      = nouveau.length > 0 && nouveau === actuel;
  const discordant     = confirmation.length > 0 && confirmation !== nouveau;
  const pretAEnvoyer   = actuel.length > 0
    && nouveau.length >= LONGUEUR_MINIMALE
    && nouveau === confirmation
    && nouveau !== actuel;

  const changer = useMutation({
    mutationFn: () => authApi.changerMotDePasse({
      motDePasseActuel:  actuel,
      nouveauMotDePasse: nouveau,
    }),
    onSuccess: () => {
      setActuel(''); setNouveau(''); setConfirm('');
      toast.success(
        'Mot de passe changé',
        'Vos autres appareils restent connectés avec la session en cours.',
      );
    },
    onError: (err) => {
      const { code, message } = extractError(err);
      toast.error(
        code === 'MOT_DE_PASSE_ACTUEL_INVALIDE' ? 'Mot de passe actuel incorrect' : 'Changement refusé',
        message,
      );
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (pretAEnvoyer) changer.mutate();
  };

  return (
    <>
      <Topbar title="Mon compte" subtitle="Vos informations et votre mot de passe" />

      <div className="ds-content" style={{ maxWidth: 620 }}>
        {/* ── Identité, en lecture seule ────────────────────── */}
        <Section title="Identité" icon={<UserRound size={14} />}>
          <Ligne label="Nom"     valeur={user?.fullName ?? '—'} />
          <Ligne label="Email"   valeur={user?.email ?? '—'} />
          <Ligne label="Rôle"    valeur={user?.role ?? '—'} />
          <Ligne label="Filiale" valeur={user?.filialeName || user?.filialeCode || '—'} />
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--tx-3)' }}>
            Ces informations sont gérées par l'administrateur du groupe. Pour les
            corriger, passez par lui.
          </div>
        </Section>

        {/* ── Mot de passe ──────────────────────────────────── */}
        <form onSubmit={onSubmit} style={{ marginTop: 16 }}>
          <Section title="Changer mon mot de passe" icon={<KeyRound size={14} />}>
          <div style={{ display: 'grid', gap: 12 }}>

            <Champ
              label="Mot de passe actuel"
              aide="Il confirme que c'est bien vous — un écran resté ouvert ne suffit pas."
            >
              <input
                className="ds-fi"
                type={afficher ? 'text' : 'password'}
                autoComplete="current-password"
                value={actuel}
                onChange={(e) => setActuel(e.target.value)}
              />
            </Champ>

            <Champ
              label="Nouveau mot de passe"
              aide={`${LONGUEUR_MINIMALE} caractères minimum. Une phrase dont vous vous souvenez vaut mieux qu'un mot compliqué que vous noterez quelque part.`}
              erreur={
                tropCourt  ? `Encore ${LONGUEUR_MINIMALE - nouveau.length} caractère(s).`
              : identique  ? 'Le nouveau mot de passe doit être différent de l\'actuel.'
              : undefined
              }
            >
              <input
                className="ds-fi"
                type={afficher ? 'text' : 'password'}
                autoComplete="new-password"
                value={nouveau}
                onChange={(e) => setNouveau(e.target.value)}
              />
            </Champ>

            <Champ
              label="Confirmer le nouveau mot de passe"
              erreur={discordant ? 'Les deux saisies ne correspondent pas.' : undefined}
            >
              <input
                className="ds-fi"
                type={afficher ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Champ>

            <button
              type="button"
              onClick={() => setAfficher((v) => !v)}
              className="ds-btn ds-btn-g ds-btn-sm"
              style={{ justifySelf: 'start' }}
            >
              {afficher ? <EyeOff size={14} /> : <Eye size={14} />}
              {afficher ? 'Masquer' : 'Afficher'} les mots de passe
            </button>

            <div className="ds-alert-strip ds-alert-i">
              <span className="ds-alert-ico"><ShieldCheck size={16} /></span>
              <div className="ds-alert-txt" style={{ fontSize: 12 }}>
                Si votre mot de passe vous a été communiqué par message,
                changez-le maintenant. Un mot de passe qui a circulé sur WhatsApp
                n'est plus un secret.
              </div>
            </div>

            <button
              type="submit"
              className="ds-btn ds-btn-p"
              disabled={!pretAEnvoyer || changer.isPending}
              style={{ justifySelf: 'start' }}
            >
              {changer.isPending ? 'Changement…' : 'Changer mon mot de passe'}
            </button>
          </div>
          </Section>
        </form>
      </div>
    </>
  );
};

const Section = ({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) => (
  <div className="ds-panel" style={{ padding: 16 }}>
    <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon} {title}
    </h3>
    {children}
  </div>
);

const Ligne = ({ label, valeur }: { label: string; valeur: string }) => (
  <div style={{ display: 'flex', gap: 12, padding: '4px 0', fontSize: 13 }}>
    <div style={{ width: 90, color: 'var(--tx-3)', flexShrink: 0 }}>{label}</div>
    <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{valeur}</div>
  </div>
);

const Champ = ({
  label, aide, erreur, children,
}: {
  label: string; aide?: string; erreur?: string; children: React.ReactNode;
}) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
    {erreur
      ? <div className="mt-1 text-[11px]" style={{ color: 'var(--danger)' }}>{erreur}</div>
      : aide && <div className="mt-1 text-[10px] text-gray-400">{aide}</div>}
  </div>
);
